import { z } from 'zod';

/**
 * Organisation & resources contracts (M1 — PM-2015/2016/2017/2018, ADR 0021).
 * Legal entities and departments are sync-owned (PM-2015): read-only over the
 * API, written only by seed/import. Roles and resources are user-editable.
 */

/**
 * Resource types are declared extensible (PM-2017, ADR 0023 pattern) — an open
 * string, never a closed enum. The two known values ship as constants.
 */
export const RESOURCE_TYPE_PERSON = 'PERSON';
export const RESOURCE_TYPE_OTHER = 'OTHER';

export const ResourceTypeSchema = z.string().min(1);
export type ResourceType = z.infer<typeof ResourceTypeSchema>;

export const EmploymentTypeSchema = z.enum(['PERMANENT', 'FIXED_TERM', 'CONTRACTOR']);
export type EmploymentType = z.infer<typeof EmploymentTypeSchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const LegalEntityResponseSchema = z.object({
  id: z.string().uuid(),
  /** TimeLog identity when synced; null for locally seeded rows. */
  externalId: z.string().nullable(),
  name: z.string(),
  lastSyncedAt: z.string().nullable(),
});
export type LegalEntityResponse = z.infer<typeof LegalEntityResponseSchema>;

export const DepartmentResponseSchema = z.object({
  id: z.string().uuid(),
  externalId: z.string().nullable(),
  number: z.string().nullable(),
  name: z.string(),
  /** Adjacency — null for a root department. Cycle-free by PM-2015 validation. */
  parentId: z.string().uuid().nullable(),
  lastSyncedAt: z.string().nullable(),
});
export type DepartmentResponse = z.infer<typeof DepartmentResponseSchema>;

export const RoleResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type RoleResponse = z.infer<typeof RoleResponseSchema>;

export const ResourceRoleResponseSchema = z.object({
  roleId: z.string().uuid(),
  roleName: z.string(),
  /** At most one primary role per resource (PM-2018). */
  isPrimary: z.boolean(),
});
export type ResourceRoleResponse = z.infer<typeof ResourceRoleResponseSchema>;

export const ResourceResponseSchema = z.object({
  id: z.string().uuid(),
  externalId: z.string().nullable(),
  name: z.string(),
  resourceType: ResourceTypeSchema,
  /** Required when resourceType='PERSON', forbidden otherwise (PM-2017). */
  employmentType: EmploymentTypeSchema.nullable(),
  legalEntityId: z.string().uuid(),
  departmentId: z.string().uuid(),
  version: z.number().int(),
  roles: z.array(ResourceRoleResponseSchema),
});
export type ResourceResponse = z.infer<typeof ResourceResponseSchema>;

export const LegalEntityListResponseSchema = z.object({
  legalEntities: z.array(LegalEntityResponseSchema),
});
export type LegalEntityListResponse = z.infer<typeof LegalEntityListResponseSchema>;

export const DepartmentListResponseSchema = z.object({
  departments: z.array(DepartmentResponseSchema),
});
export type DepartmentListResponse = z.infer<typeof DepartmentListResponseSchema>;

export const RoleListResponseSchema = z.object({ roles: z.array(RoleResponseSchema) });
export type RoleListResponse = z.infer<typeof RoleListResponseSchema>;

export const ResourceListResponseSchema = z.object({
  resources: z.array(ResourceResponseSchema),
});
export type ResourceListResponse = z.infer<typeof ResourceListResponseSchema>;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const CreateRoleRequestSchema = z.object({ name: z.string().min(1) });
export type CreateRoleRequest = z.infer<typeof CreateRoleRequestSchema>;

export const UpdateRoleRequestSchema = z.object({ name: z.string().min(1) });
export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequestSchema>;

export const CreateResourceRequestSchema = z.object({
  name: z.string().min(1),
  resourceType: ResourceTypeSchema.default(RESOURCE_TYPE_PERSON),
  employmentType: EmploymentTypeSchema.optional(),
  legalEntityId: z.string().uuid(),
  departmentId: z.string().uuid(),
  /**
   * The source-system identity, unique per tenant. Convention for TimeLog
   * tenants: the TLP **UserID** as a string — the registered-hours feed
   * matches registrations to NAMED allocations through it.
   */
  externalId: z.string().min(1).nullable().optional(),
});
export type CreateResourceRequest = z.infer<typeof CreateResourceRequestSchema>;

/** resourceType is immutable in v1 (PM-2017) — deliberately absent here. */
export const UpdateResourceRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  name: z.string().min(1).optional(),
  employmentType: EmploymentTypeSchema.nullable().optional(),
  legalEntityId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
  externalId: z.string().min(1).nullable().optional(),
});
export type UpdateResourceRequest = z.infer<typeof UpdateResourceRequestSchema>;

export const AddResourceRoleRequestSchema = z.object({
  roleId: z.string().uuid(),
  isPrimary: z.boolean().default(false),
});
export type AddResourceRoleRequest = z.infer<typeof AddResourceRoleRequestSchema>;

export const UpdateResourceRoleRequestSchema = z.object({ isPrimary: z.boolean() });
export type UpdateResourceRoleRequest = z.infer<typeof UpdateResourceRoleRequestSchema>;

// ---------------------------------------------------------------------------
// Param schemas
// ---------------------------------------------------------------------------

export const RoleIdParamsSchema = z.object({ id: z.string().uuid() });
export type RoleIdParams = z.infer<typeof RoleIdParamsSchema>;

export const ResourceIdParamsSchema = z.object({ id: z.string().uuid() });
export type ResourceIdParams = z.infer<typeof ResourceIdParamsSchema>;

export const ResourceRoleParamsSchema = z.object({
  id: z.string().uuid(),
  roleId: z.string().uuid(),
});
export type ResourceRoleParams = z.infer<typeof ResourceRoleParamsSchema>;
